import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import config from '../config/env';
import { invalidateEffective } from '../services/uiSettingsService';
import { safeStorage } from '../utils/safeStorage';
import { decodeJwtPayload, JwtPayload } from '../utils/jwt';

const buildUserFromPayload = (
  payload: JwtPayload,
  clientId: string,
): User => {
  const clientRoles = payload.resource_access?.[clientId]?.roles || [];
  const realmRoles = payload.realm_access?.roles || [];
  const groups: string[] = Array.isArray(payload.groups) ? payload.groups : [];
  const clientIds = groups
    .filter((g) => typeof g === 'string' && g.startsWith('/Clients/'))
    .map((g) => g.replace('/Clients/', ''));
  const preferredUsername =
    typeof payload.preferred_username === 'string' ? payload.preferred_username : '';
  return {
    id: typeof payload.sub === 'string' ? payload.sub : '',
    email: typeof payload.email === 'string' ? payload.email : '',
    name:
      typeof payload.name === 'string'
        ? payload.name
        : preferredUsername,
    preferredUsername,
    roles: [...new Set([...clientRoles, ...realmRoles])],
    clientIds,
  };
};

export interface User {
  id: string;
  email: string;
  name: string;
  /**
   * Keycloak `preferred_username` claim — stable, unique per realm. Use this
   * for ACL checks / allowlists. `name` is a display label and may not be
   * unique across users. Optional only so existing test fixtures that omit
   * it still type-check; production code always populates it from the JWT.
   */
  preferredUsername?: string;
  roles: string[];
  clientIds: string[];  // Client IDs from Keycloak groups (/Clients/{id})
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  // OAuth2 configuration from runtime config
  const clientId = config.oauth.clientId;
  const redirectUri = config.oauth.redirectUri;
  const authUrl = config.oauth.authUrl;
  const tokenUrl = config.oauth.tokenUrl;
  const debugEnabled = config.dev.debug;

  // Debug logging helper - memoized to prevent dependency issues
  const debugLog = useCallback((message: string, data?: any) => {
    if (debugEnabled) {
      if (data) {
        console.log(`[AUTH DEBUG] ${message}`, data);
      } else {
        console.log(`[AUTH DEBUG] ${message}`);
      }
    }
  }, [debugEnabled]);

  // Debug: Log OAuth configuration
  debugLog('OAuth Configuration:', {
    clientId,
    redirectUri,
    authUrl,
    tokenUrl,
    debugEnabled
  });

  // Generate PKCE challenge with fallback for non-secure contexts
  const generateCodeChallenge = async (verifier: string): Promise<string> => {
    // Check if Web Crypto API is available (requires HTTPS)
    if (window.crypto && window.crypto.subtle) {
      try {
        const data = new TextEncoder().encode(verifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        const digestArray = new Uint8Array(digest);
        return btoa(String.fromCharCode.apply(null, Array.from(digestArray)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      } catch (error) {
        console.warn('Web Crypto API failed, falling back to simple method:', error);
      }
    }

    // Fallback for development (not secure, but works for testing)
    console.warn('Using fallback PKCE challenge generation (development only)');
    return btoa(verifier)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const generateCodeVerifier = (): string => {
    // Check if crypto.getRandomValues is available
    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint8Array(32);
      window.crypto.getRandomValues(array);
      return btoa(String.fromCharCode.apply(null, Array.from(array)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    }

    // Fallback for development
    console.warn('Using fallback code verifier generation (development only)');
    return btoa(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const login = useCallback(async () => {
    try {
      debugLog('Login button clicked', { clientId, redirectUri, authUrl });

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store code verifier for later use (use both session and local storage for redundancy)
      sessionStorage.setItem('code_verifier', codeVerifier);
      safeStorage.setItem('temp_code_verifier', codeVerifier);

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: 'openid profile email',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: Math.random().toString(36).substring(2, 15)
      });

      const fullAuthUrl = `${authUrl}?${params.toString()}`;
      debugLog('Redirecting to Keycloak', fullAuthUrl);

      window.location.assign(fullAuthUrl);
    } catch (error) {
      console.error('Login initiation failed:', error);
      alert(`Login failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [clientId, redirectUri, authUrl, debugLog]);

  const logout = useCallback(() => {
    debugLog('Logout function called');

    // Clear local tokens first
    safeStorage.removeItem('access_token');
    safeStorage.removeItem('refresh_token');
    safeStorage.removeItem('temp_code_verifier');
    safeStorage.removeItem('expires_at');
    safeStorage.removeItem('token_type');
    sessionStorage.removeItem('code_verifier');

    debugLog('Tokens removed, setting user to null');
    setUser(null);
    setLoading(false);
    setIsProcessingToken(false);
    invalidateEffective();

    // Build Keycloak logout URL from authUrl (replace /auth with /logout)
    const logoutUrl = authUrl.replace('/protocol/openid-connect/auth', '/protocol/openid-connect/logout');

    // Redirect to Keycloak logout endpoint to end the SSO session
    const postLogoutRedirectUri = window.location.origin;
    const logoutParams = new URLSearchParams({
      client_id: clientId,
      post_logout_redirect_uri: postLogoutRedirectUri,
    });

    debugLog('Redirecting to Keycloak logout:', `${logoutUrl}?${logoutParams.toString()}`);

    // Redirect to Keycloak to end the session
    window.location.assign(`${logoutUrl}?${logoutParams.toString()}`);
  }, [debugLog, authUrl, clientId]);

  const exchangeCodeForTokens = useCallback(async (code: string) => {
    if (isProcessingToken) {
      debugLog('Token exchange already in progress, skipping...');
      return;
    }

    // Check if this code was already processed (React StrictMode protection)
    const processedKey = `processed_code_${code}`;
    if (sessionStorage.getItem(processedKey)) {
      debugLog('Code already processed, skipping duplicate exchange:', code.substring(0, 10) + '...');
      return;
    }

    // Mark code as being processed in sessionStorage (persists across component instances)
    sessionStorage.setItem(processedKey, 'true');
    setIsProcessingToken(true);

    try {
      debugLog('Attempting token exchange for code:', code.substring(0, 10) + '...');
      debugLog('Current sessionStorage keys:', Object.keys(sessionStorage));

      let codeVerifier = sessionStorage.getItem('code_verifier');
      debugLog('Code verifier from sessionStorage:', codeVerifier ? 'FOUND' : 'NOT FOUND');

      // Fallback to localStorage if sessionStorage is empty (React StrictMode issue)
      if (!codeVerifier) {
        codeVerifier = safeStorage.getItem('temp_code_verifier');
        debugLog('Code verifier from localStorage fallback:', codeVerifier ? 'FOUND' : 'NOT FOUND');
      }

      if (!codeVerifier) {
        throw new Error('Code verifier not found. This usually happens when the OAuth flow was interrupted or the page was refreshed during login. Please try logging in again.');
      }

      // Use token URL from config (supports both direct SSO and proxy for localhost)
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange response:', response.status, errorText);
        throw new Error(`Token exchange failed: ${response.status} - ${errorText}`);
      }

      const tokens = await response.json();
      debugLog('Received tokens:', {
        access_token: tokens.access_token ? 'PRESENT' : 'MISSING',
        refresh_token: tokens.refresh_token ? 'PRESENT' : 'MISSING',
        token_type: tokens.token_type,
        expires_in: tokens.expires_in
      });

      safeStorage.setItem('access_token', tokens.access_token);
      if (tokens.refresh_token) {
        safeStorage.setItem('refresh_token', tokens.refresh_token);
      }
      // Notify contexts that auth is ready so they can fetch any user-scoped
      // state (e.g. ui-settings effective defaults) that requires the token.
      invalidateEffective();
      window.dispatchEvent(new Event('app:auth-ready'));

      const payload = decodeJwtPayload(tokens.access_token);
      if (!payload) {
        debugLog('Token exchange returned an undecodable JWT — aborting');
        logout();
        setLoading(false);
        return;
      }
      debugLog('Decoded JWT payload:', payload);
      const userData = buildUserFromPayload(payload, clientId);

      debugLog('Setting user data:', userData);
      setUser(userData);

      sessionStorage.removeItem('code_verifier');
      safeStorage.removeItem('temp_code_verifier');
      // Clean up processed code marker
      sessionStorage.removeItem(`processed_code_${code}`);

      debugLog('Token exchange completed successfully');

      // CRITICAL: Clear loading state to allow UI to transition
      setLoading(false);
      debugLog('Auth state after token exchange - Loading set to false, User set to:', userData);
    } catch (error) {
      debugLog('Token exchange failed:', error);
      logout();
      // Ensure loading state is cleared even on error
      setLoading(false);
    } finally {
      setIsProcessingToken(false);
    }
  }, [clientId, redirectUri, tokenUrl, debugLog, isProcessingToken, logout]);

  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenValue = safeStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        logout();
        return;
      }

      // Use token URL from config (supports both direct SSO and proxy for localhost)
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: clientId,
          refresh_token: refreshTokenValue,
        }),
      });

      if (!response.ok) {
        logout();
        return;
      }

      const tokens = await response.json();
      safeStorage.setItem('access_token', tokens.access_token);
      if (tokens.refresh_token) {
        safeStorage.setItem('refresh_token', tokens.refresh_token);
      }

      const payload = decodeJwtPayload(tokens.access_token);
      if (!payload) {
        console.error('Token refresh returned an undecodable JWT — logging out');
        logout();
        return;
      }
      // Don't trust the refreshed token blindly: validate exp the same way the
      // initial-load path does, so a misconfigured endpoint / clock skew can't
      // establish a session from an already-expired (or non-expiring) token.
      const currentTime = Date.now() / 1000;
      if (typeof payload.exp !== 'number' || payload.exp <= currentTime) {
        console.error('Token refresh returned an expired or non-expiring JWT — logging out');
        logout();
        return;
      }
      setUser(buildUserFromPayload(payload, clientId));
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  }, [clientId, tokenUrl, logout]);

  useEffect(() => {
    const initializeAuth = async () => {
      // Skip re-initialization if already initialized and user is set
      if (authInitialized && user) {
        debugLog('Auth already initialized with user, skipping');
        return;
      }

      // Skip if token exchange is in progress
      if (isProcessingToken) {
        debugLog('Token exchange in progress, skipping initialization');
        return;
      }

      setLoading(true);

      // Handle OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      debugLog('Auth initialization:', {
        pathname: window.location.pathname,
        hasCode: !!code,
        sessionStorageKeys: Object.keys(sessionStorage),
        codeVerifierExists: !!sessionStorage.getItem('code_verifier'),
        isProcessingToken,
        authInitialized
      });

      if (code && window.location.pathname === '/auth/callback') {
        try {
          await exchangeCodeForTokens(code);
          // Don't redirect here - let the AuthCallback component handle it
          // The exchangeCodeForTokens will set user and loading states
          setAuthInitialized(true);
        } catch (error) {
          debugLog('OAuth callback failed:', error);
          // Clear any stale OAuth state and redirect to home
          sessionStorage.removeItem('code_verifier');
          safeStorage.removeItem('temp_code_verifier');
          safeStorage.removeItem('access_token');
          safeStorage.removeItem('refresh_token');
          window.history.replaceState({}, document.title, '/');
          setLoading(false);
          setAuthInitialized(true);
        }
        return;
      }

      // Check for existing token
      const accessToken = safeStorage.getItem('access_token');
      if (accessToken) {
        const payload = decodeJwtPayload(accessToken);
        if (!payload) {
          console.error('Stored access token is not decodable — logging out');
          logout();
          setLoading(false);
          setAuthInitialized(true);
        } else {
          try {
            const currentTime = Date.now() / 1000;
            if (typeof payload.exp === 'number' && payload.exp > currentTime) {
              debugLog('Valid token found, setting user');
              setUser(buildUserFromPayload(payload, clientId));
              setLoading(false);
              setAuthInitialized(true);
              invalidateEffective();
              window.dispatchEvent(new Event('app:auth-ready'));
            } else {
              // Token expired, try to refresh
              try {
                await refreshToken();
                setLoading(false);
                setAuthInitialized(true);
                invalidateEffective();
                window.dispatchEvent(new Event('app:auth-ready'));
              } catch (refreshError) {
                console.error('Refresh token failed:', refreshError);
                logout();
                setLoading(false);
                setAuthInitialized(true);
              }
            }
          } catch (error) {
            console.error('Token validation failed:', error);
            logout();
            setLoading(false);
            setAuthInitialized(true);
          }
        }
      } else {
        // No token found
        debugLog('No token found');
        setLoading(false);
        setAuthInitialized(true);
      }
    };

    initializeAuth();
  }, [exchangeCodeForTokens, refreshToken, debugLog, isProcessingToken, logout, authInitialized, user]);

  const getToken = useCallback(async (): Promise<string | null> => {
    return safeStorage.getItem('access_token');
  }, []);

  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    getToken,
  }), [user, loading, login, logout, getToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};